import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { FaRegTrashAlt } from "react-icons/fa";
import { AiOutlineSearch } from "react-icons/ai";
import { BACKEND_URL_PREFIX } from "../../utils/const";
import { arrayBufferToBase64 } from "../../actions/helper";
import { usePermission } from "../../hooks";
import HaventPermission from "../../assets/HaventPermission";
import usePageWidth from "../shared/usePageWidth";
import CustomSelect from "../CustomSelect";
import styles from "./styles.module.css";

// «Все» подставляет сам CustomSelect пустой строкой; в запрос подразделения
// уходит прежнее 'all', статус — прежнее status || 'works'. Правила отбора не меняются.
const ALL = '';

const STATUS_OPTIONS = [
  { value: 'works', name: 'Работает' },
  { value: 'dismissed', name: 'Уволен' },
  { value: 'relocated', name: 'Переведен к новому месту службы' },
  { value: 'external', name: 'Внешний совместитель' },
  { value: 'archieved', name: 'Архивная запись' },
  { value: 'service', name: 'Служебная учётная запись' },
];

const fioOf = (item) => [item.lastname, item.firstname, item.patronymic]
  .filter(Boolean)
  .join(' ')
  .trim();

// Дата рождения сортируется как дата: 01.12.1992 позже 05.11.1988
const dateKey = (value) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(String(value || ''));
  return m ? Number(`${m[3]}${m[2]}${m[1]}`) : -1;
};

const sortValue = (item, key) => {
  if (key === 'fio') return fioOf(item).toLowerCase();
  if (key === 'birthday') return dateKey(item.birthday);
  return String(item[key] ?? '').toLowerCase();
};

const isEmptyKey = (value) => value === '' || value === -1;

// поле «Поиск по ФИО» не принимает больше 50 символов
const SEARCH_MAX = 50;

// как в макете: «5 факультет» идёт перед «15 кафедра», регистр и ё/е не влияют
const COLLATE = { numeric: true, sensitivity: 'base' };

// многоточие ставится только там, где пропущено больше одной страницы
const pagerItems = (page, pages) => {
  if (pages <= 1) return [];
  const keep = new Set([1, pages, page - 1, page, page + 1]);
  if (page <= 4) [2, 3, 4, 5].forEach(i => keep.add(i));
  if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach(i => keep.add(i));
  const list = [...keep].filter(i => i >= 1 && i <= pages).sort((a, b) => a - b);

  const items = [];
  list.forEach((value, i) => {
    if (i) {
      const gap = value - list[i - 1];
      if (gap === 2) items.push(list[i - 1] + 1);
      else if (gap > 2) items.push('gap');
    }
    items.push(value);
  });
  return items;
};

const Permanent = ({
  pagesCount,
  totalCount,
  data,
  loading,
  token,
  fetchPermanentStaff,
  fetchDivisions,
  divisions,
  didMount,
}) => {

  usePageWidth('pageWide');

  const { readRight } = usePermission('Справочники', 'Постоянный состав')

  const [numberPage, setNumberPage] = useState(0);
  const [findString, setFindString] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [findDivision, setFindDivision] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  // по макету список открывается отсортированным по ФИО по возрастанию
  const [sort, setSort] = useState({ key: 'fio', dir: 'asc' });
  // фотографии текущей страницы: id → data:image/...; пока не пришла — плейсхолдер
  const [photos, setPhotos] = useState({});
  const [viewer, setViewer] = useState(null);
  const [viewerFailed, setViewerFailed] = useState(false);
  // запрос уже отправлен, но isLoading ещё не поднялся (fetch ждёт checkAndUpdateToken) —
  // без этого флага на кадр между отправкой и ответом мигает «Ничего не найдено»
  const [pending, setPending] = useState(true);

  const tableRef = useRef(null);
  const firstData = useRef(true);
  // прокрутить к началу таблицы просят при смене страницы, но выполнить это
  // можно только когда таблица снова окажется в разметке (см. эффект ниже)
  const scrollToTable = useRef(false);
  // номер «поколения» загрузки фото: ответы предыдущей страницы не должны
  // дописываться к новой — иначе миниатюры перемешиваются при быстром листании
  const photoRun = useRef(0);

  useEffect(() => {
    fetchDivisions()
    didMount()
  }, [])

  // смена любого фильтра или строки поиска сбрасывает список на первую страницу
  useEffect(() => {
    setNumberPage(0)
  }, [findDivision, findString, status])

  useEffect(() => {
    setPending(true);
    fetchPermanentStaff(numberPage, findString, findDivision || 'all', status || "works");
  }, [numberPage, findString, findDivision, status]);

  useEffect(() => {
    if (firstData.current) {
      firstData.current = false;
      return;
    }
    setPending(false);
  }, [data]);

  // поиск по ФИО с задержкой ввода 300 мс, как в макете
  useEffect(() => {
    const timeout = setTimeout(() => setFindString(searchInput), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchPhoto = useCallback(async (itemId) => {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/users/${itemId}/photo`,
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          Authorization: `Basic ${btoa(`${token}:empty`)}`,
        },
      }
    );

    if (!response.ok) return undefined;
    const buffer = await response.arrayBuffer();
    // пустое тело = фотографии нет: base64 от нуля байт даёт битую картинку
    if (!buffer || !buffer.byteLength) return undefined;
    return arrayBufferToBase64(buffer);
  }, [token]);

  // фотографии запрашиваются только для строк текущей страницы
  useEffect(() => {
    const run = photoRun.current + 1;
    photoRun.current = run;
    setPhotos({});
    if (!Array.isArray(data) || !data.length) return;

    (async () => {
      for (const item of data) {
        if (photoRun.current !== run) return;
        const src = await fetchPhoto(item.id);
        if (photoRun.current !== run) return;
        if (src) setPhotos(prev => ({ ...prev, [item.id]: src }));
      }
    })();
  }, [data, fetchPhoto]);

  const divisionOptions = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(divisions) ? divisions : [])
      .map(item => ({ value: String(item.did), name: item.name || '' }))
      .filter(({ value, name }) => {
        if (!name || value === 'undefined' || seen.has(value)) return false;
        seen.add(value);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ru', COLLATE));
  }, [divisions]);

  // сортировка идёт по загруженной странице: у метода списка нет параметра сортировки
  const rows = useMemo(() => {
    const list = Array.isArray(data) ? [...data] : [];
    if (!sort) return list;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (isEmptyKey(va) && isEmptyKey(vb)) return 0;
      if (isEmptyKey(va)) return 1;
      if (isEmptyKey(vb)) return -1;
      if (typeof va === 'number') return (va - vb) * dir;
      return va.localeCompare(vb, 'ru', COLLATE) * dir;
    });
  }, [data, sort]);

  // повторный клик по той же колонке меняет направление
  const handleSort = (key) => {
    setSort(prev => (prev && prev.key === key)
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  // Прокрутка откладывается до появления новой страницы списка: на время запроса
  // таблица уступает место загрузчику, страница становится короткой, и прокрутка,
  // запущенная в момент клика, обрывается — окно остаётся у низа таблицы.
  const handlePageChange = (page) => {
    if (page === numberPage) return;
    setNumberPage(page);
    // тот же setPending, что и в эффекте загрузки, но в одном пакете с номером
    // страницы: иначе ближайший проход эффекта увидит ещё pending === false
    // и прокрутит старую таблицу вместо новой
    setPending(true);
    scrollToTable.current = true;
  };

  useEffect(() => {
    if (!scrollToTable.current || loading || pending) return;
    scrollToTable.current = false;
    if (tableRef.current) tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loading, pending, rows]);

  const isFiltersDirty = status !== ALL || findDivision !== ALL || !!searchInput || !!findString;

  const clearFiltersHandler = () => {
    setFindDivision(ALL)
    setSearchInput("")
    setFindString("")
    setStatus(ALL)
    setNumberPage(0)
  }

  const closeViewer = useCallback(() => {
    setViewer(null);
    setViewerFailed(false);
  }, []);

  const openViewer = (item) => {
    if (!photos[item.id]) return; // пустой плейсхолдер не открывает ничего
    setViewerFailed(false);
    setViewer(item);
  };

  // пока окно открыто, страница под ним не прокручивается; ширина исчезнувшего
  // скроллбара компенсируется отступом, чтобы содержимое не дёрнулось вправо
  useEffect(() => {
    if (!viewer) return undefined;
    const { body } = document;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeViewer();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewer, closeViewer]);

  const renderValue = (value) => value ? value : <span className={styles.dash}>—</span>;

  // В столбце ФИО две строки: фамилия сверху, имя с отчеством снизу. Перенос
  // задаётся полями записи, а не шириной колонки, поэтому ряды одной высоты.
  // Пустые части не дают пустых строк: без отчества снизу остаётся одно имя,
  // без имени и отчества — одна строка с фамилией.
  const renderFio = (item) => {
    const lines = [
      item.lastname,
      [item.firstname, item.patronymic].filter(Boolean).join(' '),
    ]
      .map(line => String(line || '').trim())
      .filter(Boolean);

    if (!lines.length) return <span className={styles.dash}>—</span>;
    return lines.map((line, ind) => <div key={ind}>{line}</div>);
  };

  // каретка есть у всех сортируемых колонок; у неактивных — приглушённая ▲
  const renderHead = (key, label) => {
    const active = sort.key === key;
    return (
      <th className={styles.sortable} onClick={() => handleSort(key)} role="button">
        {label}
        <span className={active ? styles.caretOn : styles.caret}>
          {active && sort.dir === 'desc' ? '▼' : '▲'}
        </span>
      </th>
    );
  };

  const renderRow = (item) => {
    const src = photos[item.id];
    return (
      <tr key={item.id}>
        <td className={styles.fio}>{renderFio(item)}</td>
        <td className={styles.pos}>{renderValue(item.position)}</td>
        <td className={styles.soft}>{renderValue(item.division)}</td>
        <td>{renderValue(item.type)}</td>
        <td>{renderValue(item.militaryrank)}</td>
        <td className={styles.num}>{renderValue(item.birthday)}</td>
        <td className={styles.colPhoto}>
          {src
            ? (
              <button
                type="button"
                className={`${styles.ava} ${styles.avaHas}`}
                onClick={() => openViewer(item)}
                title="Открыть фотографию"
              >
                <img className={styles.avaImg} src={src} alt="" />
              </button>
            )
            : <div className={`${styles.ava} ${styles.avaNone}`} />}
        </td>
      </tr>
    )
  }

  const viewerNode = viewer && createPortal(
    <div className={styles.photoOv} onClick={closeViewer}>
      <div className={styles.photoBox} onClick={e => e.stopPropagation()}>
        <div className={styles.photoPic}>
          {photos[viewer.id] && !viewerFailed
            ? (
              <img
                src={photos[viewer.id]}
                alt={fioOf(viewer)}
                onError={() => setViewerFailed(true)}
              />
            )
            : <div className={styles.photoFail}>Фотография недоступна</div>}
        </div>
        <div className={styles.photoCap}>
          <div className={styles.photoName}>{fioOf(viewer)}</div>
          <div className={styles.photoPos}>{viewer.position || 'Должность не указана'}</div>
        </div>
        <div className={styles.photoFoot}>
          <button type="button" onClick={closeViewer}>Закрыть</button>
        </div>
      </div>
    </div>,
    document.body
  );

  if (!readRight) return <HaventPermission />

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>Постоянный состав</h1>
        <div className={styles.headRight}>
          <button
            className={styles.resetBtn}
            onClick={clearFiltersHandler}
            disabled={!isFiltersDirty}
          >
            Сбросить фильтры
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.fStatus}>
          <CustomSelect
            title="Статус"
            width="100%"
            options={STATUS_OPTIONS}
            value={status}
            setFieldValue={(name, value) => setStatus(value)}
            valuesName="status"
          />
        </div>
        <div className={styles.fDivision}>
          <CustomSelect
            title="Подразделение"
            width="100%"
            options={divisionOptions}
            value={findDivision}
            setFieldValue={(name, value) => setFindDivision(value)}
            valuesName="division"
          />
        </div>

        <div className={styles.srchWrap}>
          <AiOutlineSearch className={styles.srchIcon} size={18} />
          <input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value.slice(0, SEARCH_MAX))}
            maxLength={SEARCH_MAX}
            placeholder="Поиск по ФИО"
          />
        </div>
      </div>

      {(loading || pending) ? (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <div className={styles.tableWrap} ref={tableRef}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {renderHead('fio', 'ФИО')}
                  {renderHead('position', 'Должность')}
                  {renderHead('division', 'Подразделение')}
                  {renderHead('type', 'Тип персонала')}
                  {renderHead('militaryrank', 'Звание')}
                  {renderHead('birthday', <>Дата<br />рождения</>)}
                  <th className={styles.colPhoto}>Фото</th>
                </tr>
              </thead>
              <tbody>
                {/* ничего не найдено — сообщение в теле таблицы, шапка остаётся на месте */}
                {rows.length === 0
                  ? (
                    <tr>
                      <td colSpan={7}>
                        <div className={styles.empty}>
                          <div className={styles.emptyTitle}>Ничего не найдено</div>
                          Измените условия поиска или сбросьте фильтры
                        </div>
                      </td>
                    </tr>
                  )
                  : rows.map(renderRow)}
              </tbody>
            </table>
          </div>

          <div className={styles.footrow}>
            <span className={styles.total}>
              Найдено записей: <b>{totalCount || 0}</b>
            </span>
            {pagesCount > 1 && (
              <div className={styles.pager}>
                <button
                  disabled={numberPage === 0}
                  onClick={() => handlePageChange(numberPage - 1)}
                  aria-label="Предыдущая страница"
                >
                  ‹
                </button>
                {pagerItems(numberPage + 1, pagesCount).map((item, ind) => item === 'gap'
                  ? <span key={`gap-${ind}`} className={styles.gap}>…</span>
                  : <button
                    key={item}
                    className={item === numberPage + 1 ? styles.pageOn : undefined}
                    onClick={() => handlePageChange(item - 1)}
                  >
                    {item}
                  </button>
                )}
                <button
                  disabled={numberPage + 1 >= pagesCount}
                  onClick={() => handlePageChange(numberPage + 1)}
                  aria-label="Следующая страница"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {viewerNode}
    </div>
  )

};



export default Permanent;
